import { Caps } from "../core/Caps.js";

export interface EventMap extends HTMLElementEventMap {
    "focusinvisible": Event;
    "focusoutvisible": Event;
}

export interface EventSlot extends DisposableObject {
    readonly types: (keyof EventMap)[];
    readonly capture: boolean;
    readonly scope: HTMLElement | Document;
    readonly passive: boolean;
    readonly listener: (e: Event) => void;
};

export interface ListenerOptions<K extends keyof EventMap> {
    type: K | K[];
    scope?: HTMLElement | Document | Window;
    selector?: string | null;
    listener: (this: HTMLElement, rs: {
        event: EventMap[K],
        target: HTMLElement
    }) => any;
    passive?: boolean;
    capture?: boolean;
    thisArg?: any;
    once?: boolean;
}

class EventUtil {

    private specials: { [eventType: string]: true } = {
        mouseenter: true,
        mouseleave: true,
        pointerenter: true,
        pointerleave: true
    };

    public on<K extends keyof EventMap>(options: ListenerOptions<K>): DisposableObject;
    public on<K extends keyof EventMap>(scope: HTMLElement | Document, type: K | K[], listener: (this: HTMLElement, rs: {
        event: EventMap[K],
        target: HTMLElement
    }) => any): DisposableObject;
    public on<K extends keyof EventMap>(scope: HTMLElement | Document, type: K | K[], selector: string, listener: (this: HTMLElement, rs: {
        event: EventMap[K],
        target: HTMLElement
    }) => any): DisposableObject;
    public on<K extends keyof EventMap>(
        o: ListenerOptions<K> | (HTMLElement | Document),
        type?: K | K[],
        selector?: string | ((this: HTMLElement, rs: {
            event: EventMap[K],
            target: HTMLElement
        }) => any) | null,
        listener?: (this: HTMLElement, rs: {
            event: EventMap[K],
            target: HTMLElement
        }) => any): DisposableObject {

        var options: ListenerOptions<K>;
        if (type) {

            if (typeof selector === "string") {
                options = {
                    scope: o as (HTMLElement | Document),
                    type: type,
                    selector: selector,
                    listener: listener
                };
            } else {
                options = {
                    scope: o as (HTMLElement | Document),
                    type: type,
                    listener: selector
                };
            }
        } else {
            options = o as ListenerOptions<K>;
        }

        var oSelector = options.selector;
        var thisArg = options.thisArg;
        var once = options.once === undefined ? false : options.once;
        var disposed = false;

        var slot: EventSlot = {
            capture: options.capture === undefined ? true : options.capture,
            types: Array.isArray(options.type) ? options.type.slice() : [options.type],
            scope: options.scope || document as any,
            passive: options.passive === undefined ? false : options.passive,
            listener: (e: Event): void => {
                var n = e.target as HTMLElement;

                var isSpecial = this.specials[e.type];

                if (!oSelector) {
                    options.listener.call(thisArg === undefined ? n : thisArg,
                        {
                            event: e as any,
                            target: e.target as HTMLElement
                        });
                }

                while (n) {
                    if (typeof n.matches === "function"
                        && n.matches(oSelector)) {
                        options.listener.call(thisArg === undefined ? n : thisArg,
                            {
                                event: e as any,
                                target: n
                            });
                        if (once) {
                            slot.dispose();
                        }
                        return;
                    }
                    if (isSpecial) {
                        return;
                    }
                    n = n.parentElement;
                }
            },
            dispose: () => {
                if (disposed) {
                    return;
                }
                for (var type of slot.types) {
                    slot.scope.removeEventListener(
                        type,
                        slot.listener,
                        slot.capture);
                }

                options = null;
                thisArg = null;
                selector = null;
                slot = null;

                disposed = true;
            }
        };

        for (var eventType of slot.types) {
            slot.scope.addEventListener(eventType, slot.listener,
                Caps.passiveEventLister ?
                    {
                        passive: slot.passive,
                        capture: slot.capture
                    } : slot.capture);
        }

        return slot;
    }
}

export const event = new EventUtil();

/**
 * @deprecated Use e5.event.on() instead.
 */
export const on = event.on.bind(event);